import React, { useRef, useState, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useReactToPrint } from "react-to-print";
import apiRequest from "../services/api";
import { FaPrint, FaTimes } from "react-icons/fa";
import { numberToWords } from "../utils/numberToWords";

const InvoiceReceipt = ({ isOpen, onClose, invoiceId, type = "SALE" }) => {
  const printAreaRef = useRef();
  const [invoice, setInvoice] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const isPurchase = (type || "SALE").toUpperCase() === "PURCHASE";

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const fetchInvoiceDetails = async () => {
      if (!invoiceId) {
        setIsLoading(false);
        setInvoice(null);
        setError(null);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const response = await apiRequest(`invoices/receipt/${invoiceId}`);
        if (!cancelled) setInvoice(response?.data || null);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.message || err?.message || "Failed to fetch");
          setInvoice(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchInvoiceDetails();
    return () => {
      cancelled = true;
    };
  }, [invoiceId, isOpen]);

  const handlePrint = useReactToPrint({
    contentRef: printAreaRef,
    documentTitle: "Tax Invoice",
    pageStyle: `
      @page { size: A4; margin: 8mm; }
      @media print {
        html, body {
          margin: 0 !important; padding: 0 !important; background: white !important;
          -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;
        }
        .a4-print-wrap img[alt="MoR Verification QR"] {
          display: block !important; width: 140px !important; height: 140px !important;
          object-fit: contain !important; border: none !important;
        }
        .no-print { display: none !important; }
      }
    `,
  });

  if (!isOpen) return null;

  const verificationUrl = invoice?.id
    ? `${window.location.origin}/verify/invoice/${invoice.id}`
    : window.location.href;

  if (error) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-md p-4">
        <div className="w-full max-w-md bg-slate-800 border border-red-500/30 rounded-xl p-6 text-center shadow-xl">
          <p className="text-red-400 text-base font-semibold mb-4">
            Error: {error}
          </p>
          <button
            onClick={onClose}
            className="px-5 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm"
          >
            Close Window
          </button>
        </div>
      </div>
    );
  }

  if (isLoading || !invoice) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-md">
        <div className="text-slate-200 text-base font-semibold bg-slate-800 border border-slate-700 p-6 rounded-xl shadow-xl flex items-center gap-3">
          <div className="w-5 h-5 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
          Loading receipt details...
        </div>
      </div>
    );
  }

  // ---------- helpers ----------
  const fmt = (n, digits = 2) =>
    Number(n || 0).toLocaleString("en-US", {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });

  const fmtDateGC = (dateString) => {
    if (!dateString) return "—";

    const d = new Date(dateString);
    if (isNaN(d.getTime())) return String(dateString);

    const parts = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "Africa/Addis_Ababa",
    }).formatToParts(d);

    const get = (type) => parts.find((p) => p.type === type)?.value || "";

    return `${get("day")}-${get("month")}-${get("year")}`;
  };

  const fmtDateET = (dateString) => {
    if (!dateString) return "—";

    const d = new Date(dateString);
    if (isNaN(d.getTime())) return String(dateString);

    const parts = new Intl.DateTimeFormat("en-ET-u-ca-ethiopic-nu-latn", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "Africa/Addis_Ababa",
    }).formatToParts(d);

    const get = (type) => parts.find((p) => p.type === type)?.value || "";

    return `${get("day")}-${get("month").padStart(2, "0")}-${get("year")}`;
  };

  const fmtTime = (dateString) => {
    if (!dateString) return "—";
    try {
      return new Date(dateString).toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    } catch {
      return "—";
    }
  };

  const ethiopianDate = invoice.ethiopianDate || "—";
  const paymentMethod = String(invoice.paymentMethod || "").toUpperCase();
  const term = String(invoice.term || "").toUpperCase();
  const isCredit = term === "CREDIT" || term.includes("CREDIT");

  const invoiceTitleEn = isCredit
    ? "Credit Sales Invoice"
    : "Cash Sales Invoice / VAT / Excise Tax";
  const invoiceTitleAm = isCredit
    ? "የዱቤ ሽያጭ ደረሰኝ"
    : "የእጅ በእጅ ደረሰኝ / ታክስ / አክሳይስ ታክስ";

  const receiptType =
    invoice.receiptType ||
    (invoice.partnerIsGovernment || invoice.partnerIsGovernement
      ? "B2G"
      : invoice.partnerIsBusiness
      ? "B2B"
      : "B2C");

  // ---------- Financials ----------
  const taxableBase = Number(invoice.subtotal) || 0;
  const nonTaxableBase = Number(invoice.nonTaxableAmount) || 0;
  const discountTotal = Number(invoice.discount) || 0;
  const taxableExcise = Number(invoice.excise) || 0;
  const nonTaxableExcise = Number(invoice.nonTaxableExcise) || 0;
  const exciseTotal = taxableExcise + nonTaxableExcise;
  const vatAmount = Number(invoice.tax) || 0;
  const withholdAmount = Number(invoice.withhold) || 0;
  const vatWithholdAmount = Number(invoice.vatWithhold) || 0;
  const goodsTotal = taxableBase + nonTaxableBase + discountTotal;

  const taxableTotal = taxableBase + taxableExcise;
  const nonTaxableTotal = nonTaxableBase + nonTaxableExcise;

  const vatRate = (() => {
    if (taxableTotal > 0 && vatAmount > 0)
      return Math.round((vatAmount / taxableTotal) * 100);
    const itemRate = invoice.items?.find((i) => Number(i.taxRate) > 0)?.taxRate;
    return itemRate != null ? Number(itemRate) : 15;
  })();

  const totalInclTax = Number(invoice.totalAmount);

  const printedAt = new Date().toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const internalRef =
    invoice.invoiceNumber || invoice.documentNumber || invoice.id || "—";

  // ---------- Amharic labels (from official sample) ----------
  const L = {
    documentNo: "ደረሰኝ ቁጥር / Document No",
    date: "ቀን / Date",
    dateEC: "ቀን (ኢት) / Date",
    time: "ሰዓት / Time",
    salesReceiptType: "የሽያጭ ደረሰኝ አይነት / Sales Receipt Type",
    irn: "IRN",
    systemNumber: "ስርዓት ቁጥር / System Number",
    internalRef: "ውስጣዊ ማጣቀሻ / Internal Ref",

    from: "ከ / From",
    to: "ለ / To",
    sellersTin: "የሻጭ ቲን / Seller's TIN",
    sellersVat: "የሻጭ የተ.እ.ታ ምዝገባ ቁጥር / Seller's VAT Reg No",
    customersTin: "የደንበኛ ቲን / Customer's TIN",
    suppliersTin: "የአቅራቢ ቲን / Supplier's TIN",
    vatRegNo: "የተ.እ.ታ ምዝገባ ቁጥር / VAT Reg No",
    subTin: "ንዑስ ቲን / Sub-TIN",
    dateOfVatReg: "የተ.እ.ታ ምዝገባ ቀን / Date of VAT Registration",
    cityTown: "ከተማ / City/Town",
    woreda: "ወረዳ / Woreda",
    kebele: "ቀበሌ / Kebele",
    house: "ቤት ቁጥር / House",
    phone: "ስልክ / Phone",
    email: "ኢሜይል / Email",
    address: "አድራሻ / Address",

    // Table headers
    no: "ቁጥር / No",
    description: "መግለጫ / Description",
    nature: "ምድብ / Nature",
    uom: "መለኪያ / UOM",
    qty: "ብዛት / Qty",
    unitPrice: "የአሃድ ዋጋ / Unit Price",
    taxCode: "የታክስ ኮድ / Tax Code",
    exciseTax: "አክሳይስ ታክስ / Excise Tax",
    discount: "ቅናሽ / Discount",
    total: "ድምር / Total",

    // Totals
    totalAmount: "ጠቅላላ ድምር / Total Amount",
    excise: "አክሳይስ / Excise",
    discountAmount: "ቅናሽ መጠን / Discount Amount",
    taxableTotal: "ታክስ የሚከፈልበት ድምር / Taxable Total",
    nonTaxableTotal: "ታክስ የማይከፈልበት ድምር / Non-Taxable Total",
    vat: "ታ.እ.ታ / VAT",
    withheldAmount: "ጠቅላላ ተቀናሽ መጠን / Total Withheld Amount",
    vatWithheldAmount: "ጠቅላላ የተ.እ.ታ ተቀናሽ መጠን / Total VAT Withheld Amount",
    totalPriceInclTax: "ጠቅላላ ዋጋ ከታክስ ጋር / Total Price including Tax",

    totalInWords: "በፊደል / Total (in words)",
    paymentMethod: "የክፍያ ዘዴ / Payment Method",
    printed: "ታትሟል / Printed",
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto bg-slate-900/60 backdrop-blur-md">
      <div className="relative w-full max-w-4xl overflow-hidden rounded-2xl bg-slate-800/90 border border-slate-700/50 shadow-2xl p-5">
        {/* Toolbar */}
        <div className="no-print flex items-center justify-between pb-3 mb-3 border-b border-slate-700/50">
          <div>
            <h3 className="text-sm font-bold text-slate-100">
              Receipt Preview
            </h3>
            <p className="text-xs text-slate-400">
              {invoiceTitleEn} · {paymentMethod || "—"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold"
            >
              <FaPrint /> Print
            </button>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-200 rounded-lg bg-slate-700/50 border border-slate-600/30"
            >
              <FaTimes className="text-sm" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[82vh] pr-1">
          <div
            ref={printAreaRef}
            className="a4-print-wrap bg-white text-black px-3 py-2 font-sans text-[11px] leading-tight"
            style={{ fontFamily: "Arial, Helvetica, sans-serif" }}
          >
            {/* ---- TOP HEADER ---- */}
            <div className="flex items-start justify-between gap-3 border border-black">
              {invoice.tenantLogoUrl && (
                <div className="flex items-center justify-center p-2 shrink-0">
                  <img
                    src={invoice.tenantLogoUrl}
                    alt="Logo"
                    className="w-14 h-14 object-contain"
                  />
                </div>
              )}
              <div className="flex-1 px-3 py-2 text-center min-w-0">
                <div className="text-[15px] font-bold tracking-wide uppercase">
                  {invoice.tenantName || "—"}
                </div>
                {(invoice.branchName || invoice.tenantAddress) && (
                  <div className="text-[11px] mt-0.5 text-slate-700">
                    {[invoice.branchName, invoice.tenantAddress]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                )}
              </div>

              <table className="border-collapse text-[10px] shrink-0 w-[210px]">
                <tbody>
                  <tr>
                    <td className="border border-black border-t-0 border-r-0 px-1.5 py-0.5 font-medium whitespace-nowrap">
                      {L.documentNo}
                    </td>
                    <td className="border border-black border-t-0 px-1.5 py-0.5 font-bold text-right">
                      {invoice.documentNumber || invoice.id || "—"}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-black border-r-0 px-1.5 py-0.5 whitespace-nowrap">
                      {L.date}
                    </td>
                    <td className="border border-black px-1.5 py-0.5 text-right font-mono">
                      {fmtDateGC(invoice.invoiceDate)}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-black border-r-0 px-1.5 py-0.5 whitespace-nowrap">
                      {L.dateEC}
                    </td>
                    <td className="border border-black px-1.5 py-0.5 text-right font-mono">
                      {fmtDateET(invoice.invoiceDate)}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-black border-r-0 border-b-0 px-1.5 py-0.5 whitespace-nowrap">
                      {L.time}
                    </td>
                    <td className="border border-black border-b-0 px-1.5 py-0.5 text-right font-mono">
                      {fmtTime(invoice.invoiceDate)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* ---- TITLE + IRN | QR ---- */}
            <div className="flex items-start gap-3 mb-2">
              <div className="flex-1 min-w-0">
                <div className="text-center my-2">
                  <div className="text-[14px] font-bold underline underline-offset-2">
                    {invoiceTitleEn}
                  </div>
                  <div
                    className="text-[12px] font-semibold mt-0.5"
                    style={{
                      fontFamily:
                        '"Nyala", "Abyssinica SIL", "Ethiopia Jiret", Arial, sans-serif',
                    }}
                  >
                    {invoiceTitleAm}
                  </div>
                  <div className="text-[11px] mt-0.5">
                    {L.salesReceiptType}:{" "}
                    <span className="font-bold">{receiptType}</span>
                  </div>
                </div>

                <div className="text-[10px] space-y-0.5">
                  <div>
                    <span className="font-semibold">{L.irn}:</span>{" "}
                    <span className="font-mono break-all">
                      {invoice.irn || "—"}
                    </span>
                  </div>
                  <div>
                    <span className="font-semibold">{L.systemNumber}:</span>{" "}
                    <span className="font-mono">
                      {invoice.mrc || invoice.systemNumber || "—"}
                    </span>
                  </div>
                  <div>
                    <span className="font-semibold">{L.internalRef}:</span>{" "}
                    <span className="font-mono">{internalRef}</span>
                  </div>
                </div>
              </div>

              <div className="shrink-0 flex items-center justify-center self-stretch bg-white p-0">
                {invoice?.qr ? (
                  <img
                    src={
                      String(invoice.qr).trim().startsWith("data:")
                        ? String(invoice.qr).trim()
                        : `data:image/png;base64,${String(invoice.qr).trim()}`
                    }
                    alt="MoR Verification QR"
                    width={140}
                    height={140}
                    draggable={false}
                    style={{
                      display: "block",
                      width: 140,
                      height: 140,
                      objectFit: "contain",
                      imageRendering: "pixelated",
                    }}
                  />
                ) : (
                  <QRCodeSVG
                    value={verificationUrl}
                    size={140}
                    level="H"
                    includeMargin={true}
                    bgColor="#FFFFFF"
                    fgColor="#000000"
                  />
                )}
              </div>
            </div>

            {/* ---- FROM / TO ---- */}
            <div className="grid grid-cols-2 border border-black mb-2 text-[10px]">
              <div className="border-r border-black p-2 min-w-0">
                <div className="font-bold mb-1">
                  {L.from}: {invoice.tenantName || "—"}
                </div>
                <div className="space-y-0.5 text-slate-800">
                  <div>
                    {L.sellersTin}:{" "}
                    <span className="font-mono font-semibold">
                      {invoice.tenantTin || "N/A"}
                    </span>
                  </div>
                  <div>
                    {L.sellersVat}:{" "}
                    <span className="font-mono">
                      {invoice.tenantVat || "N/A"}
                    </span>
                  </div>
                  <div>
                    {L.subTin}:{" "}
                    <span className="font-mono">
                      {invoice.tenantSubTin || "N/A"}
                    </span>
                  </div>
                  <div>
                    {L.dateOfVatReg}: {invoice.tenantVatRegDate || "N/A"}
                  </div>
                  <div>
                    {L.cityTown}: {invoice.tenantCity || "N/A"} · {L.woreda}:{" "}
                    {invoice.tenantWoreda || "N/A"}
                  </div>
                  <div>
                    {L.kebele}: {invoice.tenantKebele || "N/A"} · {L.house}:{" "}
                    {invoice.tenantHouseNo || "N/A"}
                  </div>
                  <div>
                    {L.phone}:{" "}
                    <span className="font-mono">
                      {invoice.tenantPhone || "N/A"}
                    </span>
                  </div>
                  <div className="break-all">
                    {L.email}: {invoice.tenantEmail || "N/A"}
                  </div>
                </div>
              </div>

              <div className="p-2 min-w-0">
                <div className="font-bold mb-1">
                  {L.to}:{" "}
                  {invoice.partnerName ||
                    (isPurchase ? "Supplier" : "Customer")}
                </div>
                <div className="space-y-0.5 text-slate-800">
                  <div>
                    {isPurchase ? L.suppliersTin : L.customersTin}:{" "}
                    <span className="font-mono font-semibold">
                      {invoice.partnerTin || "N/A"}
                    </span>
                  </div>
                  <div>
                    {L.vatRegNo}:{" "}
                    <span className="font-mono">
                      {invoice.partnerVat || "N/A"}
                    </span>
                  </div>
                  <div>
                    {L.subTin}:{" "}
                    <span className="font-mono">
                      {invoice.partnerSubTin || "N/A"}
                    </span>
                  </div>
                  <div>
                    {L.dateOfVatReg}: {invoice.partnerVatRegDate || "N/A"}
                  </div>
                  <div>
                    {L.cityTown}: {invoice.partnerCity || "N/A"} · {L.woreda}:{" "}
                    {invoice.partnerWoreda || "N/A"}
                  </div>
                  <div>
                    {L.kebele}: {invoice.partnerKebele || "N/A"} · {L.house}:{" "}
                    {invoice.partnerHouseNo || "N/A"}
                  </div>
                  <div>
                    {L.phone}:{" "}
                    <span className="font-mono">
                      {invoice.partnerPhone || "N/A"}
                    </span>
                  </div>
                  <div className="break-all">
                    {L.email}: {invoice.partnerEmail || "N/A"}
                  </div>
                  {invoice.partnerAddress && (
                    <div className="break-words">
                      {L.address}: {invoice.partnerAddress}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ---- ITEMS TABLE ---- */}
            <table className="w-full border-collapse border border-black text-[10px] mb-0">
              <thead>
                <tr className="bg-slate-100">
                  <th className="border border-black px-1 py-1 text-center w-7">
                    {L.no}
                  </th>
                  <th className="border border-black px-1 py-1 text-left">
                    {L.description}
                  </th>
                  <th className="border border-black px-1 py-1 text-center w-12">
                    {L.nature}
                  </th>
                  <th className="border border-black px-1 py-1 text-center w-10">
                    {L.uom}
                  </th>
                  <th className="border border-black px-1 py-1 text-center w-10">
                    {L.qty}
                  </th>
                  <th className="border border-black px-1 py-1 text-right w-16">
                    {L.unitPrice}
                  </th>
                  <th className="border border-black px-1 py-1 text-center w-12">
                    {L.taxCode}
                  </th>
                  <th className="border border-black px-1 py-1 text-right w-14">
                    {L.exciseTax}
                  </th>
                  <th className="border border-black px-1 py-1 text-right w-12">
                    {L.discount}
                  </th>
                  <th className="border border-black px-1 py-1 text-right w-16">
                    {L.total}
                  </th>
                </tr>
              </thead>
              <tbody>
                {invoice.items?.length > 0 ? (
                  invoice.items.map((item, index) => {
                    const unitPrice =
                      Number(
                        isPurchase ? item.purchasePrice : item.sellingPrice
                      ) || 0;
                    const lineTotal =
                      Number(isPurchase ? item.totalCost : item.totalPrice) ||
                      0;
                    const excise = Number(item.excise || 0);
                    const discount = Number(item.discount || 0);
                    const nature =
                      item.nature || item.productType || item.type || "goods";
                    const uom = item.unit || item.uom || "PCS";
                    const taxCode =
                      item.taxCode || item.vatCode || item.taxType || "VAT15";

                    return (
                      <tr key={item.id || index}>
                        <td className="border border-black px-1 py-0.5 text-center font-mono">
                          {index + 1}
                        </td>
                        <td className="border border-black px-1 py-0.5 break-words">
                          {item.productName || item.description || "—"}
                        </td>
                        <td className="border border-black px-1 py-0.5 text-center">
                          {nature}
                        </td>
                        <td className="border border-black px-1 py-0.5 text-center font-mono">
                          {uom}
                        </td>
                        <td className="border border-black px-1 py-0.5 text-center font-mono">
                          {fmt(item.quantity || 0, 2)}
                        </td>
                        <td className="border border-black px-1 py-0.5 text-right font-mono">
                          {fmt(unitPrice)}
                        </td>
                        <td className="border border-black px-1 py-0.5 text-center font-mono">
                          {taxCode}
                        </td>
                        <td className="border border-black px-1 py-0.5 text-right font-mono">
                          {fmt(excise)}
                        </td>
                        <td className="border border-black px-1 py-0.5 text-right font-mono">
                          {fmt(discount)}
                        </td>
                        <td className="border border-black px-1 py-0.5 text-right font-mono font-semibold">
                          {fmt(lineTotal)}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={10}
                      className="border border-black px-2 py-3 text-center text-slate-500 italic"
                    >
                      No items
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* ---- TOTALS ---- */}
            <div className="flex justify-end border border-black border-t-0 mb-2">
              <table className="border-collapse text-[10px] w-[340px]">
                <tbody>
                  <tr>
                    <td className="border border-black border-t-0 border-l-0 px-2 py-0.5">
                      {L.totalAmount}
                    </td>
                    <td className="border border-black border-t-0 border-r-0 px-2 py-0.5 text-right font-mono font-semibold w-28">
                      {fmt(goodsTotal)}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-black border-l-0 px-2 py-0.5">
                      {L.excise}
                    </td>
                    <td className="border border-black border-r-0 px-2 py-0.5 text-right font-mono">
                      {fmt(exciseTotal)}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-black border-l-0 px-2 py-0.5">
                      {L.discountAmount}
                    </td>
                    <td className="border border-black border-r-0 px-2 py-0.5 text-right font-mono">
                      {fmt(discountTotal)}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-black border-l-0 px-2 py-0.5">
                      {L.taxableTotal}
                    </td>
                    <td className="border border-black border-r-0 px-2 py-0.5 text-right font-mono">
                      {fmt(taxableTotal)}
                    </td>
                  </tr>
                  {nonTaxableTotal > 0 && (
                    <tr>
                      <td className="border border-black border-l-0 px-2 py-0.5">
                        {L.nonTaxableTotal}
                      </td>
                      <td className="border border-black border-r-0 px-2 py-0.5 text-right font-mono">
                        {fmt(nonTaxableTotal)}
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td className="border border-black border-l-0 px-2 py-0.5">
                      {L.vat} {vatRate}%
                    </td>
                    <td className="border border-black border-r-0 px-2 py-0.5 text-right font-mono">
                      {fmt(vatAmount)}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-black border-l-0 px-2 py-0.5">
                      {L.withheldAmount}
                    </td>
                    <td className="border border-black border-r-0 px-2 py-0.5 text-right font-mono">
                      {fmt(withholdAmount)}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-black border-l-0 px-2 py-0.5">
                      {L.vatWithheldAmount}
                    </td>
                    <td className="border border-black border-r-0 px-2 py-0.5 text-right font-mono">
                      {fmt(vatWithholdAmount)}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-black border-l-0 px-2 py-1 font-bold">
                      {L.totalPriceInclTax}
                    </td>
                    <td className="border border-black border-r-0 px-2 py-1 text-right font-mono font-bold text-[11px]">
                      {fmt(totalInclTax)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* ---- AMOUNT IN WORDS ---- */}
            <div className="border border-black px-2 py-1.5 mb-2 text-[10px]">
              <span className="font-semibold">{L.totalInWords}: </span>
              <span className="font-bold capitalize">
                {numberToWords(totalInclTax)}
              </span>
            </div>

            {/* ---- PAYMENT METHOD ---- */}
            <div className="border border-black px-2 py-1.5 mb-2 text-[10px]">
              <span className="font-semibold">{L.paymentMethod}: </span>
              <span className="font-bold uppercase tracking-wide">
                {isCredit ? "NA" : paymentMethod || "CASH"}
              </span>
            </div>

            {/* ---- FOOTER ---- */}
            <div className="flex items-center justify-between text-[9px] text-slate-600 pt-1 border-t border-slate-300 mt-1">
              <div>
                {invoice.salesPerson
                  ? ` · Sales: ${String(invoice.salesPerson).trim()}`
                  : ""}
              </div>
              <div>
                {L.printed}: {printedAt}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceReceipt;
